/**
 * Humanizes a date to show relative time (e.g., "5 seconds ago", "1 hour ago", "4 days ago")
 * - Assumes server returns UTC (ISO 8601 with Z). If a timezone is missing, we assume UTC to avoid TZ drift.
 * - Uses Intl.RelativeTimeFormat so strings are localized to the user's browser locale/timezone.
 */
export function humanizeDate(date: string | Date): string {
  const now = new Date()
  const target = parseDateAssumeUtc(date)

  const diffMs = target.getTime() - now.getTime() // positive => in future

  // Near-zero differences are treated as just now
  if (Math.abs(diffMs) < 1000 * 5) return new Intl.RelativeTimeFormat(navigator.language, { numeric: 'auto' }).format(0, 'second')

  const rtf = new Intl.RelativeTimeFormat(navigator.language, { numeric: 'auto' })

  const msPerSecond = 1000
  const msPerMinute = msPerSecond * 60
  const msPerHour = msPerMinute * 60
  const msPerDay = msPerHour * 24
  const msPerWeek = msPerDay * 7
  const msPerMonth = msPerDay * 30
  const msPerYear = msPerDay * 365

  const abs = Math.abs(diffMs)

  if (abs < msPerMinute) {
    return rtf.format(Math.round(diffMs / msPerSecond), 'second')
  } else if (abs < msPerHour) {
    return rtf.format(Math.round(diffMs / msPerMinute), 'minute')
  } else if (abs < msPerDay) {
    return rtf.format(Math.round(diffMs / msPerHour), 'hour')
  } else if (abs < msPerWeek) {
    return rtf.format(Math.round(diffMs / msPerDay), 'day')
  } else if (abs < msPerMonth) {
    return rtf.format(Math.round(diffMs / msPerWeek), 'week')
  } else if (abs < msPerYear) {
    return rtf.format(Math.round(diffMs / msPerMonth), 'month')
  } else {
    return rtf.format(Math.round(diffMs / msPerYear), 'year')
  }
}

/**
 * Humanizes by calendar day granularity.
 * Returns: "today", "tomorrow", "yesterday", "in N days", or "N days ago".
 * Uses the user's local timezone for day boundaries.
 */
export function humanizeDay(date: string | Date): string {
  const target = parseDateAssumeUtc(date)
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startOfTarget = new Date(target.getFullYear(), target.getMonth(), target.getDate())
  const diffMs = startOfTarget.getTime() - startOfToday.getTime()
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'today'
  if (diffDays === 1) return 'tomorrow'
  if (diffDays === -1) return 'yesterday'
  if (diffDays > 1) return `in ${diffDays} days`
  return `${Math.abs(diffDays)} days ago`
}

/**
 * Public helper: parse a date input, assuming UTC when timezone is missing.
 */
export function toUtcDate(input: string | Date): Date {
  return parseDateAssumeUtc(input)
}

/**
 * Parses various date inputs, assuming UTC if the incoming string lacks an explicit timezone.
 */
function parseDateAssumeUtc(input: string | Date): Date {
  if (input instanceof Date) return input
  const s = input.trim()
  // If the string already includes Z or an explicit numeric offset, trust it
  if (/Z|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s)
  // If it looks like an ISO string without timezone, assume UTC by appending Z
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return new Date(s + 'Z')
  // If it is just a date (YYYY-MM-DD), treat as midnight UTC
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s + 'T00:00:00Z')
  // Fallback to native parsing
  return new Date(s)
}
